import { Plus } from "lucide-mui"
import { DateTime } from "luxon"
import React from "react"

import i18n from "@/i18n"

import { type ReservationBestPick } from "../../../reservation_best_picks"

import { TS_STYLES } from "./styles"

export type BestPicksSlotProps = {
  picks: ReservationBestPick[]
  /** Accept a suggested (table + window) pick: sets tableId + startTime + endTime at once. */
  onAcceptPick: (pick: { tableId: string; startTime: string; endTime: string }) => void
}

// `setZone: true` keeps the venue offset carried by the ISO string; a plain parse would convert to
// the device zone and mislabel the window for guests whose device tz ≠ venue tz.
const formatWindow = (startTime: string, endTime: string): string =>
  `${DateTime.fromISO(startTime, { setZone: true }).toFormat("HH:mm")} - ${DateTime.fromISO(endTime, { setZone: true }).toFormat("HH:mm")}`

/**
 * The "Best available picks" empty-state strip (behavior board 1283-51228): a section title above a
 * horizontally-scrollable row of green chips, each a (table + earliest free window) suggestion with a
 * circular `+`. Tapping a chip accepts that pick — the state machine commits table + start + end at
 * once (decision #4). Renders nothing when there are no picks; the two dropdown pills below still
 * cover manual selection.
 */
const BestPicksSlot: React.FC<BestPicksSlotProps> = ({ picks, onAcceptPick }) => {
  if (picks.length === 0) return null

  return (
    <div className={TS_STYLES.bestPicksSlot} data-testid="public-reservation-modal-mobile-best-picks-slot">
      <p className={TS_STYLES.bestPicksTitle}>{i18n.t("app.public.venue.table_selection.best_picks_title")}</p>

      <div className={TS_STYLES.bestPicksScroller}>
        {picks.map((pick) => (
          <button
            key={pick.tableId}
            type="button"
            className={TS_STYLES.bestPicksChip}
            aria-label={i18n.t("app.public.venue.table_selection.best_picks_accept", { table: pick.tableName })}
            onClick={() => onAcceptPick({ tableId: pick.tableId, startTime: pick.startTime, endTime: pick.endTime })}
            data-testid={`public-reservation-modal-mobile-best-picks-chip-${pick.tableId}`}
          >
            <span className={TS_STYLES.bestPicksChipText}>
              <span className={TS_STYLES.bestPicksChipName}>{pick.tableName}</span>
              <span className={TS_STYLES.bestPicksChipWindow}>{formatWindow(pick.startTime, pick.endTime)}</span>
            </span>
            <span
              className={TS_STYLES.bestPicksChipPlus}
              data-testid={`public-reservation-modal-mobile-best-picks-accept-${pick.tableId}`}
            >
              <Plus className="h-3 w-3" strokeWidth={2.5} />
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

export default BestPicksSlot
